// src/Components/canvas_drag_N_DROP/SettingsPanel.tsx

import React, { useRef, useState, useEffect, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import { Node } from "reactflow";
import { useTerraformResourceDetail, TerraformAttribute } from "../../hooks/useTerraformResourceDetail";
import BlockEditor from "./BlockEditor";
import { FiMaximize2, FiInfo, FiCheck } from "react-icons/fi";
import FieldWithSuggestions from "./FieldWithSuggestions";
import { getResourceConfig, getFieldSuggestions } from "../../Config/resourceConfigProvider";
import { getCommonValuesForField, detectProviderFromResource } from "../../Config/cloudCommonValues";
import { useArchitectureTabs } from "../../context/ArchitectureTabsContext";

// Icon imports for resolving icons from resource types
import { azureIconsByType } from "../../Config/azure-iconsByType";
import { azureIconsByTypeTop100 } from "../../Config/azure/azure-iconsByType.top100";
import { awsIconsByTypeTop100 } from "../../Config/aws/aws-iconsByType.top100";
import { iconsByType as gcpIcons } from "../../Config/iconsByType";

const azureDefaultIcon = "/icons/azure/Microsoft Azure.svg";

/**
 * Resolve icon path from resource type
 */
function resolveIconFromType(type: string): string {
  // AWS icons
  if (type.startsWith('aws_')) {
    return (awsIconsByTypeTop100 as any)?.[type] || '/icons/aws/Arch_General_48.svg';
  }
  // GCP icons
  if (type.startsWith('google_')) {
    return (gcpIcons as any)?.[type] || '/icons/gcp/google-cloud.svg';
  }
  // Azure icons (default)
  return (
    (azureIconsByTypeTop100 as any)?.[type] ||
    (azureIconsByType as any)?.[type] ||
    azureDefaultIcon
  );
}

/**
 * Get resolved icon - use existing icon if valid path, otherwise resolve from type
 */
function getResolvedIcon(icon: string | undefined, type: string | undefined): string {
  // If icon is a valid path (starts with / or http), use it
  if (icon && (icon.startsWith('/') || icon.startsWith('http'))) {
    return icon;
  }
  // Otherwise resolve from type
  if (type) {
    return resolveIconFromType(type);
  }
  return azureDefaultIcon;
}

interface Props {
  node: Node;
  onClose: () => void;
  onUpdate: (updatedNode: Node) => void;
  onExpandToDock?: (node: Node) => void;
  mode?: "floating" | "dock";
  availableNodes?: Node[];
}

declare global {
  interface Window {
    $RefreshSig$?: any;
    $RefreshReg$?: any;
  }
}
if (typeof window !== "undefined") {
  window.$RefreshSig$ = window.$RefreshSig$ || (() => (t: any) => t);
  window.$RefreshReg$ = window.$RefreshReg$ || (() => {});
}

function Badge({
  children,
  color = "#eef2ff",
  textColor = "#3730a3",
}: {
  children: React.ReactNode;
  color?: string;
  textColor?: string;
}) {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "1px 6px",
        borderRadius: 6,
        background: color,
        color: textColor,
        fontSize: 10,
        lineHeight: "16px",
        marginLeft: 6,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

/**
 * Helper function to detect if a parameter is a resource reference
 * and return the expected resource type
 */
/**
 * Get resource reference type based on parameter name AND current resource's provider
 * This ensures AWS resources suggest AWS references, Azure suggests Azure, etc.
 */
function getResourceReferenceType(paramName: string, currentResourceType?: string): string | null {
  // Detect provider from current resource type
  const provider = currentResourceType?.startsWith('aws_') ? 'aws' :
                   currentResourceType?.startsWith('google_') ? 'gcp' :
                   currentResourceType?.startsWith('azurerm_') ? 'azure' : 'azure';

  // Provider-specific mappings
  const azureMap: Record<string, string> = {
    'server_id': 'azurerm_mssql_server',
    'resource_group_name': 'azurerm_resource_group',
    'virtual_network_name': 'azurerm_virtual_network',
    'subnet_id': 'azurerm_subnet',
    'network_security_group_id': 'azurerm_network_security_group',
    'storage_account_id': 'azurerm_storage_account',
    'key_vault_id': 'azurerm_key_vault',
    'app_service_plan_id': 'azurerm_app_service_plan',
    'application_insights_id': 'azurerm_application_insights',
    'public_ip_address_id': 'azurerm_public_ip',
    'load_balancer_id': 'azurerm_lb',
    'application_gateway_id': 'azurerm_application_gateway',
  };

  const awsMap: Record<string, string> = {
    'vpc_id': 'aws_vpc',
    'subnet_id': 'aws_subnet',
    'subnet_ids': 'aws_subnet',
    'security_group_ids': 'aws_security_group',
    'vpc_security_group_ids': 'aws_security_group',
    'security_groups': 'aws_security_group',
    'db_instance_identifier': 'aws_db_instance',
    'db_subnet_group_name': 'aws_db_subnet_group',
    'kms_key_id': 'aws_kms_key',
    'kms_key_arn': 'aws_kms_key',
    'iam_role_arn': 'aws_iam_role',
    'iam_instance_profile': 'aws_iam_instance_profile',
    'load_balancer_arn': 'aws_lb',
    'target_group_arn': 'aws_lb_target_group',
    'route_table_id': 'aws_route_table',
    'internet_gateway_id': 'aws_internet_gateway',
    'nat_gateway_id': 'aws_nat_gateway',
    'instance_id': 'aws_instance',
    'launch_template_id': 'aws_launch_template',
    'ami': 'aws_ami',
  };

  const gcpMap: Record<string, string> = {
    'folder_id': 'google_folder',          // For projects and nested folders
    'parent': 'google_folder',             // Alternative parent reference
    'network': 'google_compute_network',
    'subnetwork': 'google_compute_subnetwork',
    'project': 'google_project',
    'instance_template': 'google_compute_instance_template',
    'target_pool': 'google_compute_target_pool',
    'backend_service': 'google_compute_backend_service',
    'ssl_certificate': 'google_compute_ssl_certificate',
    'service_account': 'google_service_account',
    'disk': 'google_compute_disk',
  };

  // Return mapping based on provider
  if (provider === 'aws') {
    return awsMap[paramName] || null;
  } else if (provider === 'gcp') {
    return gcpMap[paramName] || null;
  } else {
    return azureMap[paramName] || null;
  }
}

/**
 * Generate Terraform resource reference suggestions from available nodes
 */
function getResourceReferences(nodes: Node[], resourceType: string, currentNodeId: string): string[] {
  return nodes
    .filter(n =>
      n.id !== currentNodeId &&
      n.type === 'custom' &&
      (n.data?.type === resourceType || n.data?.resourceType === resourceType)
    )
    .map(n => {
      // Use label first (what user sees), then fallback to params.name or name
      const label = n.data?.label || n.data?.params?.name || n.data?.name || 'resource';
      // Format Terraform reference: resource_type.resource_name.id
      const tfName = label.toLowerCase().replace(/[^a-z0-9_]/g, '_');
      return `\${${resourceType}.${tfName}.id}`;
    });
}

/**
 * Special handling for resource_group_name - return just the name, not the ID
 * Priority: 1) Parent group (if node is inside a group), 2) All other groups
 */
function getResourceGroupNames(nodes: Node[], currentNode: Node): string[] {
  const suggestions: string[] = [];

  // PRIORITY 1: If node is inside a resource group, suggest that group first
  if (currentNode.parentNode) {
    const parentGroup = nodes.find(n =>
      n.id === currentNode.parentNode &&
      n.type === 'group' &&
      (n.data?.type === 'azurerm_resource_group' || n.data?.resourceType === 'azurerm_resource_group')
    );

    if (parentGroup) {
      // Use label first (what user sees), then fallback to name or params.name
      const label = parentGroup.data?.label || parentGroup.data?.params?.name || parentGroup.data?.name || 'rg-default';
      const tfName = label.toLowerCase().replace(/[^a-z0-9_]/g, '_');
      suggestions.push(`\${azurerm_resource_group.${tfName}.name}`);
    }
  }

  // PRIORITY 2: Add all other resource groups
  const otherGroups = nodes
    .filter(n =>
      n.id !== currentNode.id &&
      n.id !== currentNode.parentNode && // Exclude parent (already added)
      n.type === 'group' &&
      (n.data?.type === 'azurerm_resource_group' || n.data?.resourceType === 'azurerm_resource_group')
    )
    .map(n => {
      // Use label first (what user sees), then fallback to name or params.name
      const label = n.data?.label || n.data?.params?.name || n.data?.name || 'rg-default';
      const tfName = label.toLowerCase().replace(/[^a-z0-9_]/g, '_');
      return `\${azurerm_resource_group.${tfName}.name}`;
    });

  return [...suggestions, ...otherGroups];
}

/**
 * Special handling for virtual_network_name - returns .name reference (not .id)
 * Priority: 1) Direct parent VNet, 2) Grandparent VNet, 3) All other VNets in canvas
 */
function getVirtualNetworkNames(nodes: Node[], currentNode: Node): string[] {
  const suggestions: string[] = [];
  const seen = new Set<string>();

  const addVnet = (vnetNode: Node) => {
    if (seen.has(vnetNode.id)) return;
    seen.add(vnetNode.id);
    const label = vnetNode.data?.label || vnetNode.data?.params?.name || vnetNode.data?.name || 'vnet';
    const tfName = label.toLowerCase().replace(/[^a-z0-9_]/g, '_');
    suggestions.push(`\${azurerm_virtual_network.${tfName}.name}`);
  };

  if (currentNode.parentNode) {
    // PRIORITY 1: direct parent is a VNet (subnet directly inside VNet group)
    const parent = nodes.find(n =>
      n.id === currentNode.parentNode &&
      n.type === 'group' &&
      (n.data?.type === 'azurerm_virtual_network' || n.data?.resourceType === 'azurerm_virtual_network')
    );
    if (parent) {
      addVnet(parent);
    } else {
      // PRIORITY 2: grandparent is a VNet (e.g. subnet inside RG which is inside a VNet)
      const parentNode = nodes.find(n => n.id === currentNode.parentNode);
      if (parentNode?.parentNode) {
        const grandparent = nodes.find(n =>
          n.id === parentNode.parentNode &&
          n.type === 'group' &&
          (n.data?.type === 'azurerm_virtual_network' || n.data?.resourceType === 'azurerm_virtual_network')
        );
        if (grandparent) addVnet(grandparent);
      }
    }
  }

  // PRIORITY 3 (fallback): all VNet groups in the canvas
  nodes
    .filter(n =>
      n.type === 'group' &&
      (n.data?.type === 'azurerm_virtual_network' || n.data?.resourceType === 'azurerm_virtual_network')
    )
    .forEach(addVnet);

  return suggestions;
}

/**
 * Special handling for AWS VPC ID - return just the ID
 * Priority: 1) Parent VPC (if node is inside a VPC), 2) All other VPCs
 */
function getVpcIds(nodes: Node[], currentNode: Node): string[] {
  const suggestions: string[] = [];

  // PRIORITY 1: If node is inside a VPC, suggest that VPC first
  if (currentNode.parentNode) {
    const parentVpc = nodes.find(n =>
      n.id === currentNode.parentNode &&
      n.type === 'group' &&
      (n.data?.type === 'aws_vpc' || n.data?.resourceType === 'aws_vpc')
    );

    if (parentVpc) {
      const label = parentVpc.data?.label || parentVpc.data?.params?.name || parentVpc.data?.name || 'vpc-default';
      const tfName = label.toLowerCase().replace(/[^a-z0-9_]/g, '_');
      suggestions.push(`\${aws_vpc.${tfName}.id}`);
    }
  }

  // PRIORITY 2: Add all other VPCs
  const otherVpcs = nodes
    .filter(n =>
      n.id !== currentNode.id &&
      n.id !== currentNode.parentNode &&
      n.type === 'group' &&
      (n.data?.type === 'aws_vpc' || n.data?.resourceType === 'aws_vpc')
    )
    .map(n => {
      const label = n.data?.label || n.data?.params?.name || n.data?.name || 'vpc-default';
      const tfName = label.toLowerCase().replace(/[^a-z0-9_]/g, '_');
      return `\${aws_vpc.${tfName}.id}`;
    });

  return [...suggestions, ...otherVpcs];
}

/**
 * Special handling for GCP Project - return the project ID
 * Priority: 1) Parent project (if node is inside a project), 2) All other projects
 */
function getProjectIds(nodes: Node[], currentNode: Node): string[] {
  const suggestions: string[] = [];

  // PRIORITY 1: If node is inside a project, suggest that project first
  if (currentNode.parentNode) {
    const parentProject = nodes.find(n =>
      n.id === currentNode.parentNode &&
      n.type === 'group' &&
      (n.data?.type === 'google_project' || n.data?.resourceType === 'google_project')
    );

    if (parentProject) {
      const label = parentProject.data?.label || parentProject.data?.params?.name || parentProject.data?.name || 'project-default';
      const tfName = label.toLowerCase().replace(/[^a-z0-9_]/g, '_');
      suggestions.push(`\${google_project.${tfName}.project_id}`);
    }
  }

  // PRIORITY 2: Add all other projects
  const otherProjects = nodes
    .filter(n =>
      n.id !== currentNode.id &&
      n.id !== currentNode.parentNode &&
      n.type === 'group' &&
      (n.data?.type === 'google_project' || n.data?.resourceType === 'google_project')
    )
    .map(n => {
      const label = n.data?.label || n.data?.params?.name || n.data?.name || 'project-default';
      const tfName = label.toLowerCase().replace(/[^a-z0-9_]/g, '_');
      return `\${google_project.${tfName}.project_id}`;
    });

  return [...suggestions, ...otherProjects];
}

/**
 * Special handling for GCP Folder ID
 * Priority: 1) Parent folder (if node is inside a folder), 2) All other folders
 * Used for: google_project.folder_id, google_folder.parent
 */
function getFolderIds(nodes: Node[], currentNode: Node): string[] {
  const suggestions: string[] = [];

  // PRIORITY 1: If node is inside a folder, suggest that folder first
  if (currentNode.parentNode) {
    const parentFolder = nodes.find(n =>
      n.id === currentNode.parentNode &&
      n.type === 'group' &&
      (n.data?.type === 'google_folder' || n.data?.resourceType === 'google_folder')
    );

    if (parentFolder) {
      const label = parentFolder.data?.label || parentFolder.data?.params?.name || parentFolder.data?.name || 'folder-default';
      const tfName = label.toLowerCase().replace(/[^a-z0-9_]/g, '_');
      suggestions.push(`\${google_folder.${tfName}.name}`);
    }
  }

  // PRIORITY 2: Add all other folders
  const otherFolders = nodes
    .filter(n =>
      n.id !== currentNode.id &&
      n.id !== currentNode.parentNode &&
      n.type === 'group' &&
      (n.data?.type === 'google_folder' || n.data?.resourceType === 'google_folder')
    )
    .map(n => {
      const label = n.data?.label || n.data?.params?.name || n.data?.name || 'folder-default';
      const tfName = label.toLowerCase().replace(/[^a-z0-9_]/g, '_');
      return `\${google_folder.${tfName}.name}`;
    });

  return [...suggestions, ...otherFolders];
}

/**
 * Special handling for AWS Subnet ID
 * Priority: 1) Parent subnet (if node is inside a subnet), 2) All other subnets
 */
function getSubnetIds(nodes: Node[], currentNode: Node): string[] {
  const suggestions: string[] = [];

  // PRIORITY 1: If node is inside a subnet container, suggest that subnet first
  if (currentNode.parentNode) {
    const parentSubnet = nodes.find(n =>
      n.id === currentNode.parentNode &&
      n.type === 'group' &&
      (n.data?.type === 'aws_subnet' || n.data?.resourceType === 'aws_subnet')
    );

    if (parentSubnet) {
      const label = parentSubnet.data?.label || parentSubnet.data?.params?.name || parentSubnet.data?.name || 'subnet-default';
      const tfName = label.toLowerCase().replace(/[^a-z0-9_]/g, '_');
      suggestions.push(`\${aws_subnet.${tfName}.id}`);
    }
  }

  // PRIORITY 2: Add all AWS subnets (both group and custom nodes)
  const otherSubnets = nodes
    .filter(n =>
      n.id !== currentNode.id &&
      n.id !== currentNode.parentNode &&
      (n.data?.type === 'aws_subnet' || n.data?.resourceType === 'aws_subnet')
    )
    .map(n => {
      const label = n.data?.label || n.data?.params?.name || n.data?.name || 'subnet-default';
      const tfName = label.toLowerCase().replace(/[^a-z0-9_]/g, '_');
      return `\${aws_subnet.${tfName}.id}`;
    });

  return [...suggestions, ...otherSubnets];
}

/**
 * Special handling for Azure Subnet ID
 * Priority: 1) Parent subnet (if node is inside a subnet), 2) All other subnets
 */
function getAzureSubnetIds(nodes: Node[], currentNode: Node): string[] {
  const suggestions: string[] = [];

  // PRIORITY 1: If node is inside a subnet container, suggest that subnet first
  if (currentNode.parentNode) {
    const parentSubnet = nodes.find(n =>
      n.id === currentNode.parentNode &&
      n.type === 'group' &&
      (n.data?.type === 'azurerm_subnet' || n.data?.resourceType === 'azurerm_subnet')
    );

    if (parentSubnet) {
      const label = parentSubnet.data?.label || parentSubnet.data?.params?.name || parentSubnet.data?.name || 'subnet-default';
      const tfName = label.toLowerCase().replace(/[^a-z0-9_]/g, '_');
      suggestions.push(`\${azurerm_subnet.${tfName}.id}`);
    }
  }

  // PRIORITY 2: Add all Azure subnets (both group and custom nodes)
  const otherSubnets = nodes
    .filter(n =>
      n.id !== currentNode.id &&
      n.id !== currentNode.parentNode &&
      (n.data?.type === 'azurerm_subnet' || n.data?.resourceType === 'azurerm_subnet')
    )
    .map(n => {
      const label = n.data?.label || n.data?.params?.name || n.data?.name || 'subnet-default';
      const tfName = label.toLowerCase().replace(/[^a-z0-9_]/g, '_');
      return `\${azurerm_subnet.${tfName}.id}`;
    });

  return [...suggestions, ...otherSubnets];
}

/**
 * Special handling for GCP Subnetwork
 * Priority: 1) Parent subnetwork (if node is inside a subnetwork), 2) All other subnetworks
 */
function getGcpSubnetworkIds(nodes: Node[], currentNode: Node): string[] {
  const suggestions: string[] = [];

  // PRIORITY 1: If node is inside a subnetwork container, suggest that subnetwork first
  if (currentNode.parentNode) {
    const parentSubnet = nodes.find(n =>
      n.id === currentNode.parentNode &&
      n.type === 'group' &&
      (n.data?.type === 'google_compute_subnetwork' || n.data?.resourceType === 'google_compute_subnetwork')
    );

    if (parentSubnet) {
      const label = parentSubnet.data?.label || parentSubnet.data?.params?.name || parentSubnet.data?.name || 'subnetwork-default';
      const tfName = label.toLowerCase().replace(/[^a-z0-9_]/g, '_');
      suggestions.push(`\${google_compute_subnetwork.${tfName}.self_link}`);
    }
  }

  // PRIORITY 2: Add all GCP subnetworks (both group and custom nodes)
  const otherSubnets = nodes
    .filter(n =>
      n.id !== currentNode.id &&
      n.id !== currentNode.parentNode &&
      (n.data?.type === 'google_compute_subnetwork' || n.data?.resourceType === 'google_compute_subnetwork')
    )
    .map(n => {
      const label = n.data?.label || n.data?.params?.name || n.data?.name || 'subnetwork-default';
      const tfName = label.toLowerCase().replace(/[^a-z0-9_]/g, '_');
      return `\${google_compute_subnetwork.${tfName}.self_link}`;
    });

  return [...suggestions, ...otherSubnets];
}

/**
 * Special handling for GCP Network
 * Priority: 1) Parent network (if node is inside a network), 2) All other networks
 */
function getGcpNetworkIds(nodes: Node[], currentNode: Node): string[] {
  const suggestions: string[] = [];

  // PRIORITY 1: If node is inside a network container, suggest that network first
  if (currentNode.parentNode) {
    // Check parent and grandparent for network
    const parentNode = nodes.find(n => n.id === currentNode.parentNode);
    const networkNode = parentNode?.type === 'group' &&
      (parentNode?.data?.type === 'google_compute_network' || parentNode?.data?.resourceType === 'google_compute_network')
        ? parentNode
        : parentNode?.parentNode
          ? nodes.find(n => n.id === parentNode.parentNode && n.type === 'group' &&
              (n.data?.type === 'google_compute_network' || n.data?.resourceType === 'google_compute_network'))
          : null;

    if (networkNode) {
      const label = networkNode.data?.label || networkNode.data?.params?.name || networkNode.data?.name || 'network-default';
      const tfName = label.toLowerCase().replace(/[^a-z0-9_]/g, '_');
      suggestions.push(`\${google_compute_network.${tfName}.self_link}`);
    }
  }

  // PRIORITY 2: Add all GCP networks
  const otherNetworks = nodes
    .filter(n =>
      n.id !== currentNode.id &&
      n.id !== currentNode.parentNode &&
      (n.data?.type === 'google_compute_network' || n.data?.resourceType === 'google_compute_network')
    )
    .map(n => {
      const label = n.data?.label || n.data?.params?.name || n.data?.name || 'network-default';
      const tfName = label.toLowerCase().replace(/[^a-z0-9_]/g, '_');
      return `\${google_compute_network.${tfName}.self_link}`;
    });

  return [...suggestions, ...otherNetworks];
}

const FABRIC_SKUS = [
  { sku: 'Trial', cus: 64,  monthlyEur: 0,     useCase: 'Essai gratuit (60 jours)', trial: true },
  { sku: 'F2',   cus: 2,    monthlyEur: 262,   useCase: 'Dev / Test' },
  { sku: 'F4',   cus: 4,    monthlyEur: 524,   useCase: 'Petit projet' },
  { sku: 'F8',   cus: 8,    monthlyEur: 1048,  useCase: 'Équipe data' },
  { sku: 'F16',  cus: 16,   monthlyEur: 2096,  useCase: 'Analytics moyen' },
  { sku: 'F32',  cus: 32,   monthlyEur: 4192,  useCase: 'Analytics large' },
  { sku: 'F64',  cus: 64,   monthlyEur: 8384,  useCase: 'Enterprise' },
  { sku: 'F128', cus: 128,  monthlyEur: 16768, useCase: 'Enterprise+' },
];

export default function SettingsPanel({
  node,
  onClose,
  onUpdate,
  onExpandToDock,
  mode = "floating",
  availableNodes = [],
}: Props) {
  const { t } = useTranslation('canvas');
  const { activeTabId } = useArchitectureTabs();
  const panelRef = useRef<HTMLDivElement>(null);

  // Panel dimensions (must match wrapperStyle below)
  const PANEL_WIDTH = 350;
  const PANEL_HEIGHT = 580;

  // Calculate centered position
  const getCenteredPosition = () => ({
    x: Math.max(0, (window.innerWidth - PANEL_WIDTH) / 2),
    y: Math.max(0, (window.innerHeight - PANEL_HEIGHT) / 2),
  });

  const [position, setPosition] = useState(getCenteredPosition);
  const [dragging, setDragging] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  const isDock = mode === "dock";

  // Recenter when switching to floating mode
  useEffect(() => {
    if (!isDock) {
      setPosition(getCenteredPosition());
    }
  }, [isDock]);

  const tfKey: string | undefined =
    ((node.data as any)?.type as string | undefined) ||
    ((node.data as any)?.tfKey as string | undefined);

  const isFabricCapacity  = tfKey === 'fabric_capacity';
  const isFabricWorkspace = tfKey === 'fabric_workspace';
  const isFabricDomain    = tfKey === 'fabric_domain';
  const isPurviewAccount  = tfKey === 'azurerm_purview_account';
  const isFabricItem      = !!(tfKey?.startsWith('fabric_') && !isFabricCapacity && !isFabricWorkspace && !isFabricDomain) ||
                            tfKey === 'azurerm_purview_account' || tfKey === 'azurerm_policy_assignment';

  const [label, setLabel] = useState<string>(
    (node.data as any).label ||
      (node.data as any).type ||
      (node.data as any).tfKey ||
      t('settingsPanel.defaultTitle')
  );

  /** Charger les params initiaux depuis node.data.params */
  const [params, setParams] = useState<Record<string, any>>(
    (node.data as any)?.params || {}
  );

  // État pour afficher/masquer les propriétés avancées
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Toast de confirmation après sauvegarde
  const [saveSuccess, setSaveSuccess] = useState(false);

  // État pour la recherche de champs
  const [searchQuery, setSearchQuery] = useState("");

  // Utiliser le nouveau hook qui charge depuis le schéma Terraform complet
  const { data: tfData, loading: tfLoading, error: tfError } = useTerraformResourceDetail(tfKey);

  // Utiliser uniquement les données Terraform (plus de fallback vers l'ancien système catalog)
  const resourceData = tfData;
  const isLoading = tfLoading;
  const resourceError = tfError;

  // Fallback mode when backend is not available
  const isBackendUnavailable = resourceError && (resourceError.includes('404') || resourceError.includes('resource_not_found') || resourceError.includes('fetch_failed'));

  // Get local resource configuration for suggestions
  const resourceConfig = useMemo(() => tfKey ? getResourceConfig(tfKey) : null, [tfKey]);

  // Fabric workspace options for workspaceId dropdown
  const fabricWorkspaceOptions = useMemo(() =>
    availableNodes
      .filter(n => n.data?.type === 'fabric_workspace' || n.data?.resourceType === 'fabric_workspace')
      .map(n => ({
        id: n.id,
        label: n.data?.label || n.data?.params?.display_name || n.data?.params?.name || n.id,
      })),
    [availableNodes]
  );

  // Fabric capacity options for capacityId dropdown
  const fabricCapacityOptions = useMemo(() =>
    availableNodes
      .filter(n => n.data?.type === 'fabric_capacity' || n.data?.resourceType === 'fabric_capacity')
      .map(n => ({
        id: n.id,
        label: n.data?.label || n.data?.params?.display_name || n.data?.params?.name || n.id,
        sku: n.data?.params?.sku || '',
      })),
    [availableNodes]
  );

  // Helper function to remove provider prefixes
  const removeProviderPrefix = (key: string) => {
    return key.replace(/^(azurerm_|aws_|google_)/, "");
  };

  const title = useMemo(() => {
    if (tfData?.resourceType) {
      // Utiliser le nom de la ressource Terraform
      return removeProviderPrefix(tfData.resourceType);
    }
    if (resourceConfig?.displayName) return resourceConfig.displayName;
    if (tfKey) return removeProviderPrefix(tfKey);
    return t('settingsPanel.defaultTitle');
  }, [tfData, resourceConfig, tfKey, t]);

  // Ref pour éviter l'initialisation multiple
  const isInitializedRef = useRef(false);

  /** Init params depuis backend, sans location */
  useEffect(() => {
    // Ne pas réinitialiser si déjà fait
    if (isInitializedRef.current) return;

    // Priorité aux données Terraform
    if (tfData?.properties) {
      setParams(prevParams => {
        const next: Record<string, any> = { ...prevParams };
        let hasChanges = false;

        // Liste des propriétés importantes à toujours initialiser
        const importantProps = ['name', 'region', 'zone', 'project', 'resource_group_name', 'database_version', 'deletion_protection'];

        for (const p of tfData.properties) {
          if (p.name === "location") continue;

          // Initialiser si le champ n'existe pas déjà
          if (!(p.name in next)) {
            // Initialiser les champs importants OU les champs requis non-computed
            if (importantProps.includes(p.name) || (p.required && !p.computed)) {
              const localField = resourceConfig?.fields.find(f => f.key === p.name);
              next[p.name] = localField?.default ?? "";
              hasChanges = true;
            }
          }
        }

        // Aussi initialiser les champs depuis nestedBlocks (ex: tier dans settings)
        if (tfData.nestedBlocks) {
          for (const block of tfData.nestedBlocks) {
            if (block.attributes) {
              for (const attr of block.attributes) {
                if (!(attr.name in next)) {
                  // Initialiser les champs importants OU requis
                  if (importantProps.includes(attr.name) || attr.required) {
                    const localField = resourceConfig?.fields.find(f => f.key === attr.name);
                    next[attr.name] = localField?.default ?? "";
                    hasChanges = true;
                  }
                }
              }
            }
          }
        }

        // SPECIAL: Auto-initialize resource_group_name if node is inside a resource group (Azure)
        if (!next.resource_group_name && node.parentNode && availableNodes.length > 0) {
          const parentGroup = availableNodes.find(n =>
            n.id === node.parentNode &&
            n.type === 'group' &&
            (n.data?.type === 'azurerm_resource_group' || n.data?.resourceType === 'azurerm_resource_group')
          );

          if (parentGroup) {
            // Use label first (what user sees), then fallback to name or params.name
            const label = parentGroup.data?.label || parentGroup.data?.params?.name || parentGroup.data?.name || 'rg-default';
            const tfName = label.toLowerCase().replace(/[^a-z0-9_]/g, '_');
            next.resource_group_name = `\${azurerm_resource_group.${tfName}.name}`;
            hasChanges = true;
          }
        }

        // SPECIAL: Auto-initialize vpc_id if node is inside a VPC (AWS)
        if (!next.vpc_id && node.parentNode && availableNodes.length > 0) {
          const parentVpc = availableNodes.find(n =>
            n.id === node.parentNode &&
            n.type === 'group' &&
            (n.data?.type === 'aws_vpc' || n.data?.resourceType === 'aws_vpc')
          );

          if (parentVpc) {
            const label = parentVpc.data?.label || parentVpc.data?.params?.name || parentVpc.data?.name || 'vpc-default';
            const tfName = label.toLowerCase().replace(/[^a-z0-9_]/g, '_');
            next.vpc_id = `\${aws_vpc.${tfName}.id}`;
            hasChanges = true;
          }
        }

        // SPECIAL: Auto-initialize project if node is inside a Project (GCP)
        if (!next.project && node.parentNode && availableNodes.length > 0) {
          const parentProject = availableNodes.find(n =>
            n.id === node.parentNode &&
            n.type === 'group' &&
            (n.data?.type === 'google_project' || n.data?.resourceType === 'google_project')
          );

          if (parentProject) {
            const label = parentProject.data?.label || parentProject.data?.params?.name || parentProject.data?.name || 'project-default';
            const tfName = label.toLowerCase().replace(/[^a-z0-9_]/g, '_');
            next.project = `\${google_project.${tfName}.project_id}`;
            hasChanges = true;
          }
        }

        // Auto-fill workspaceId from parentNode for Fabric items (template path)
        if (isFabricItem && !next.workspaceId && node.parentNode) {
          const wsNode = availableNodes.find(n =>
            n.id === node.parentNode &&
            (n.data?.type === 'fabric_workspace' || n.data?.resourceType === 'fabric_workspace')
          );
          if (wsNode) {
            next.workspaceId = node.parentNode;
            hasChanges = true;
          }
        }

        // Marquer comme initialisé
        if (hasChanges) {
          isInitializedRef.current = true;
        }

        return hasChanges ? next : prevParams;
      });
    }
  }, [tfData, resourceConfig, node.parentNode, availableNodes]);


  /** Update resource_group_name when node is moved to a different parent group (Azure) */
  useEffect(() => {
    if (!node.parentNode || availableNodes.length === 0) return;

    const parentGroup = availableNodes.find(n =>
      n.id === node.parentNode &&
      n.type === 'group' &&
      (n.data?.type === 'azurerm_resource_group' || n.data?.resourceType === 'azurerm_resource_group')
    );

    if (parentGroup && params.resource_group_name) {
      // Use label first (what user sees), then fallback to name or params.name
      const label = parentGroup.data?.label || parentGroup.data?.params?.name || parentGroup.data?.name || 'rg-default';
      const tfName = label.toLowerCase().replace(/[^a-z0-9_]/g, '_');
      const expectedValue = `\${azurerm_resource_group.${tfName}.name}`;

      // Only update if the current value doesn't match the parent group
      if (params.resource_group_name !== expectedValue) {
        setParams(prev => ({ ...prev, resource_group_name: expectedValue }));
      }
    }
  }, [node.parentNode, availableNodes]);

  /** Update vpc_id when node is moved to a different parent VPC (AWS) */
  useEffect(() => {
    if (!node.parentNode || availableNodes.length === 0) return;

    const parentVpc = availableNodes.find(n =>
      n.id === node.parentNode &&
      n.type === 'group' &&
      (n.data?.type === 'aws_vpc' || n.data?.resourceType === 'aws_vpc')
    );

    if (parentVpc && params.vpc_id) {
      const label = parentVpc.data?.label || parentVpc.data?.params?.name || parentVpc.data?.name || 'vpc-default';
      const tfName = label.toLowerCase().replace(/[^a-z0-9_]/g, '_');
      const expectedValue = `\${aws_vpc.${tfName}.id}`;

      if (params.vpc_id !== expectedValue) {
        setParams(prev => ({ ...prev, vpc_id: expectedValue }));
      }
    }
  }, [node.parentNode, availableNodes]);

  /** Update project when node is moved to a different parent Project (GCP) */
  useEffect(() => {
    if (!node.parentNode || availableNodes.length === 0) return;

    const parentProject = availableNodes.find(n =>
      n.id === node.parentNode &&
      n.type === 'group' &&
      (n.data?.type === 'google_project' || n.data?.resourceType === 'google_project')
    );

    if (parentProject && params.project) {
      const label = parentProject.data?.label || parentProject.data?.params?.name || parentProject.data?.name || 'project-default';
      const tfName = label.toLowerCase().replace(/[^a-z0-9_]/g, '_');
      const expectedValue = `\${google_project.${tfName}.project_id}`;

      if (params.project !== expectedValue) {
        setParams(prev => ({ ...prev, project: expectedValue }));
      }
    }
  }, [node.parentNode, availableNodes]);

  /** Échap = fermer */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  /** Drag handling */
  const onMouseDown = (e: React.MouseEvent) => {
    if (!panelRef.current || isDock) return;
    const panel = panelRef.current;
    setOffset({ x: e.clientX - panel.offsetLeft, y: e.clientY - panel.offsetTop });
    setDragging(true);
    e.preventDefault();
    e.stopPropagation();
  };
  const onMouseMove = (e: MouseEvent) => {
    if (!dragging) return;
    setPosition({ x: e.clientX - offset.x, y: e.clientY - offset.y });
  };
  const onMouseUp = () => setDragging(false);
  useEffect(() => {
    if (dragging) {
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    } else {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    }
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, [dragging, offset]);

  const handleParamChange = (key: string, value: any) => {
    setParams((prev) => ({ ...prev, [key]: value }));
  };

  /** Auto-save params with debounce for cost estimation updates */
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const currentNodeParams = (node.data as any)?.params || {};
      const hasChanges = JSON.stringify(params) !== JSON.stringify(currentNodeParams);

      if (hasChanges && Object.keys(params).length > 0) {
        const updatedNode: Node = {
          ...node,
          data: { ...node.data, label, params, tfKey },
        };
        // Auto-nest Fabric items when workspaceId changes
        if (isFabricItem && params.workspaceId && params.workspaceId !== node.parentNode) {
          const wsNode = availableNodes.find(n => n.id === params.workspaceId);
          if (wsNode) {
            updatedNode.parentNode = params.workspaceId;
            updatedNode.extent = 'parent' as const;
            updatedNode.position = {
              x: Math.max(10, node.position.x - wsNode.position.x),
              y: Math.max(10, node.position.y - wsNode.position.y),
            };
          }
        }
        // Auto-nest Fabric workspace when capacityId changes
        if (isFabricWorkspace && params.capacityId && params.capacityId !== node.parentNode) {
          const capNode = availableNodes.find(n => n.id === params.capacityId);
          if (capNode) {
            updatedNode.parentNode = params.capacityId;
            updatedNode.extent = 'parent' as const;
            updatedNode.position = {
              x: Math.max(10, node.position.x - capNode.position.x),
              y: Math.max(10, node.position.y - capNode.position.y),
            };
          }
        }
        onUpdate(updatedNode);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [params, label]);

  const handleSave = () => {
    const updatedNode: Node = {
      ...node,
      data: {
        ...node.data,
        label,
        params,
        tfKey,
      },
    };

    // Auto-nest Fabric items into the selected workspace via parentNode
    const targetWsId = params.workspaceId ||
      (node.parentNode && fabricWorkspaceOptions.some(w => w.id === node.parentNode) ? node.parentNode : '');
    if (isFabricItem && targetWsId && targetWsId !== node.parentNode) {
      const wsNode = availableNodes.find(n => n.id === targetWsId);
      if (wsNode) {
        updatedNode.parentNode = targetWsId;
        updatedNode.extent = 'parent' as const;
        updatedNode.position = {
          x: Math.max(10, node.position.x - wsNode.position.x),
          y: Math.max(10, node.position.y - wsNode.position.y),
        };
      }
    }

    // Auto-nest Fabric workspace into the selected capacity via parentNode
    const targetCapId = params.capacityId ||
      (node.parentNode && fabricCapacityOptions.some(c => c.id === node.parentNode) ? node.parentNode : '');
    if (isFabricWorkspace && targetCapId && targetCapId !== node.parentNode) {
      const capNode = availableNodes.find(n => n.id === targetCapId);
      if (capNode) {
        updatedNode.parentNode = targetCapId;
        updatedNode.extent = 'parent' as const;
        updatedNode.position = {
          x: Math.max(10, node.position.x - capNode.position.x),
          y: Math.max(10, node.position.y - capNode.position.y),
        };
      }
    }

    onUpdate(updatedNode);
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 2500);
  };

  /** rendu dynamique pour les propriétés Terraform */
  const renderTerraformInput = (p: TerraformAttribute) => {
    const v = params[p.name] ?? "";

    // PRIORITY 1: Check if this field is a resource reference (server_id, resource_group_name, etc.)
    // Pass the current resource type to get provider-aware suggestions
    const referencedResourceType = getResourceReferenceType(p.name, tfKey);
    if (referencedResourceType) {
      // Special handling for hierarchical containers: Resource Groups (Azure), VPCs (AWS), Subnets (AWS/Azure), Folders/Projects (GCP)
      const isAwsResource = tfKey?.startsWith('aws_');
      const isGcpResource = tfKey?.startsWith('google_');
      const isAzureResource = tfKey?.startsWith('azurerm_');
      const resourceReferences =
        p.name === 'resource_group_name' ? getResourceGroupNames(availableNodes, node) :
        p.name === 'vpc_id' ? getVpcIds(availableNodes, node) :
        p.name === 'project' ? getProjectIds(availableNodes, node) :
        (p.name === 'folder_id' && isGcpResource) ? getFolderIds(availableNodes, node) :
        (p.name === 'parent' && isGcpResource) ? getFolderIds(availableNodes, node) :
        (p.name === 'subnet_id' && isAwsResource) ? getSubnetIds(availableNodes, node) :
        (p.name === 'subnet_id' && isAzureResource) ? getAzureSubnetIds(availableNodes, node) :
        (p.name === 'virtual_network_name' && isAzureResource) ? getVirtualNetworkNames(availableNodes, node) :
        (p.name === 'subnetwork' && isGcpResource) ? getGcpSubnetworkIds(availableNodes, node) :
        (p.name === 'network' && isGcpResource) ? getGcpNetworkIds(availableNodes, node) :
        getResourceReferences(availableNodes, referencedResourceType, node.id);

      // Always show FieldWithSuggestions for reference fields, even if empty
      return (
        <FieldWithSuggestions
          label=""
          value={String(v)}
          onChange={(value) => handleParamChange(p.name, value)}
          suggestions={resourceReferences}
          placeholder={`Sélectionnez ${referencedResourceType.replace(/^(azurerm_|aws_|google_)/, '')}`}
          required={p.required}
          architectureId={activeTabId || undefined}
        />
      );
    }

    // Vérifier si une configuration locale existe pour ce champ
    const localField = resourceConfig?.fields.find(f => f.key === p.name);

    // Si le champ a des options définies, utiliser FieldWithSuggestions pour supporter les variables
    if (localField?.options && localField.options.length > 0) {
      return (
        <FieldWithSuggestions
          label=""
          value={String(v)}
          onChange={(value) => handleParamChange(p.name, value)}
          suggestions={localField.options}
          placeholder={t('settingsPanel.fields.selectPlaceholder')}
          required={p.required}
          architectureId={activeTabId || undefined}
        />
      );
    }

    // Vérifier si des valeurs communes existent pour ce champ selon le provider
    const provider = detectProviderFromResource(tfKey || '');
    const commonValues = provider ? getCommonValuesForField(p.name, provider) : null;

    // Si des valeurs communes existent, utiliser FieldWithSuggestions pour supporter les variables
    if (commonValues && commonValues.length > 0) {
      return (
        <FieldWithSuggestions
          label=""
          value={String(v)}
          onChange={(value) => handleParamChange(p.name, value)}
          suggestions={commonValues}
          placeholder={t('settingsPanel.fields.selectPlaceholder')}
          required={p.required}
          architectureId={activeTabId || undefined}
        />
      );
    }

    // Le type peut être une chaîne ou un tableau (ex: ["map", "string"])
    const typeStr = Array.isArray(p.type) ? p.type[0] : p.type;
    const typeValue = (typeof typeStr === 'string' ? typeStr.toLowerCase() : 'string');

    if (typeValue === "bool" || typeValue === "boolean") {
      return (
        <FieldWithSuggestions
          label=""
          value={String(v)}
          onChange={(value) => {
            // Convert string to boolean if needed
            if (value === "true" || value === "false") {
              handleParamChange(p.name, value === "true");
            } else {
              // Keep as string (for variable references like {{my_bool_var}})
              handleParamChange(p.name, value);
            }
          }}
          suggestions={['true', 'false']}
          placeholder={t('settingsPanel.fields.selectPlaceholder')}
          required={p.required}
          architectureId={activeTabId || undefined}
        />
      );
    }
    if (typeValue === "number" || typeValue.includes("int") || typeValue.includes("float")) {
      return (
        <FieldWithSuggestions
          label=""
          value={String(v)}
          onChange={(value) => {
            // Try to parse as number if it's numeric
            if (value && !isNaN(Number(value))) {
              handleParamChange(p.name, Number(value));
            } else {
              // Keep as string (for variable references like {{my_number_var}})
              handleParamChange(p.name, value);
            }
          }}
          suggestions={[]}
          placeholder={t('settingsPanel.fields.enterField', { field: p.name })}
          required={p.required}
          architectureId={activeTabId || undefined}
        />
      );
    }

    // Utiliser les suggestions du champ local si disponibles
    let suggestions: string[] = localField?.suggestions || [];

    // Sinon, suggestions par défaut pour certains champs
    if (suggestions.length === 0) {
      if (p.name === 'name') {
        const baseName = tfKey ? removeProviderPrefix(tfKey) : 'resource';
        suggestions.push(`${baseName}-myapp`, `${baseName}-prod`);
      } else if (p.name === 'resource_group_name') {
        suggestions.push('rg-prod', 'rg-dev', 'rg-infrastructure');
      }
    }

    return (
      <FieldWithSuggestions
        label=""
        value={String(v)}
        onChange={(value) => handleParamChange(p.name, value)}
        suggestions={suggestions}
        placeholder={t('settingsPanel.fields.enterField', { field: p.name })}
        required={p.required}
        architectureId={activeTabId || undefined}
      />
    );
  };

  const wrapperStyle: React.CSSProperties = isDock
    ? {
        position: "relative",
        width: "100%",
        height: "100%",
        background: "#fff",
        display: "flex",
        flexDirection: "column",
      }
    : {
        position: "absolute",
        top: position.y,
        left: position.x,
        width: PANEL_WIDTH,
        height: PANEL_HEIGHT,
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 10,
        boxShadow: "0 12px 28px rgba(0,0,0,0.12)",
        zIndex: 1001,
        userSelect: "none",
        display: "flex",
        flexDirection: "column",
      };
  return (
    <div
      ref={panelRef}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      style={wrapperStyle}
      role="dialog"
      aria-label={t('settingsPanel.title', { title })}
      aria-modal="true"
      tabIndex={-1}
    >
      <style>{`@keyframes fadeInUp{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:translateY(0)}}`}</style>
      {/* HEADER */}
      <div
        onMouseDown={isDock ? undefined : onMouseDown}
        style={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          cursor: isDock ? "default" : "move",
          backgroundColor: "#f8fafc",
          padding: "8px 12px",
          borderBottom: "1px solid #e5e7eb",
          fontWeight: 700,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontSize: 13,
        }}
      >
        {t('settingsPanel.title', { title })}
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {!isDock && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onExpandToDock?.(node);
              }}
              style={{ background: "none", border: "none", cursor: "pointer", color: "#374151" }}
              title={t('settingsPanel.buttons.expandTitle')}
              aria-label={t('settingsPanel.buttons.expand')}
            >
              <FiMaximize2 size={18} />
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            style={{
              background: "none",
              border: "none",
              fontSize: 18,
              cursor: "pointer",
              lineHeight: 1,
              color: "#374151",
            }}
            aria-label={t('settingsPanel.buttons.close')}
            title={t('settingsPanel.buttons.close')}
          >
            ✕
          </button>
        </div>
      </div>

      {/* BODY */}
      <div style={{ padding: 12, overflowY: "auto", flex: 1 }}>
        {/* Barre de recherche */}
        <div style={{ marginBottom: 12 }}>
          <input
            type="text"
            placeholder={t('settingsPanel.search.placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              ...inputStyle,
              marginTop: 0,
              background: "#f9fafb",
              borderColor: searchQuery ? "#3b82f6" : "#e5e7eb",
              transition: "border-color 0.2s"
            }}
          />
          {searchQuery && (
            <div style={{ marginTop: 6, fontSize: 11, color: "#6b7280", display: "flex", alignItems: "center", gap: 6 }}>
              <span>{t('settingsPanel.search.active', { query: searchQuery })}</span>
              <button
                onClick={() => setSearchQuery("")}
                style={{
                  background: "none",
                  border: "none",
                  color: "#3b82f6",
                  cursor: "pointer",
                  fontSize: 11,
                  textDecoration: "underline"
                }}
              >
                {t('settingsPanel.buttons.clear')}
              </button>
            </div>
          )}
        </div>
        {isLoading && <div style={{ fontSize: 13, color: "#64748b" }}>{t('settingsPanel.messages.loading')}</div>}

        {/* Message d'information si backend non disponible */}
        {isBackendUnavailable && (
          <div style={{
            padding: 10,
            marginBottom: 12,
            background: '#FEF3C7',
            border: '1px solid #F59E0B',
            borderRadius: 6,
            fontSize: 12,
            color: '#92400E'
          }}
            dangerouslySetInnerHTML={{ __html: t('settingsPanel.messages.basicMode') }}
          />
        )}

        {/* Afficher le contenu même si erreur backend */}
        {(tfData || isBackendUnavailable || !resourceError) && (
          <>
            {/* Infos Node */}
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12 }}>
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 8,
                  background: "#f3f6ff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src={getResolvedIcon((node.data as any).icon, (node.data as any).type || (node.data as any).resourceType)}
                  alt={title}
                  style={{ width: 22, height: 22, objectFit: "contain" }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  {tfKey || (node.data as any).type}
                </div>
                <div style={{ fontSize: 11, color: "#9ca3af" }}>ID: {node.id}</div>
              </div>
            </div>

            {/* Label */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontWeight: 700, fontSize: 12 }}>{t('settingsPanel.fields.label')}</label>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                style={inputStyle}
              />
            </div>

            {/* FABRIC CAPACITY — SKU Selector */}
            {isFabricCapacity && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontWeight: 700, fontSize: 12, display: 'block', marginBottom: 8 }}>
                  SKU (Capacité Fabric)
                </label>
                {/* Carte Trial pleine largeur */}
                {(() => {
                  const trial = FABRIC_SKUS.find(s => (s as any).trial);
                  if (!trial) return null;
                  const isSelected = (params.sku || 'F2') === trial.sku;
                  return (
                    <div
                      onClick={() => handleParamChange('sku', trial.sku)}
                      style={{
                        border: `2px solid ${isSelected ? '#10B981' : '#D1FAE5'}`,
                        borderRadius: 8, padding: '8px 12px', cursor: 'pointer', marginBottom: 6,
                        background: isSelected ? '#ECFDF5' : '#F0FDF4',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        transition: 'all 0.15s',
                      }}
                    >
                      <div>
                        <span style={{ fontWeight: 700, fontSize: 13, color: '#065F46' }}>Trial</span>
                        <span style={{ fontSize: 10, color: '#6B7280', marginLeft: 6 }}>64 CUs</span>
                        <div style={{ fontSize: 10, color: '#6B7280', marginTop: 2 }}>Essai gratuit (60 jours)</div>
                      </div>
                      <span style={{
                        background: '#10B981', color: 'white', fontWeight: 700,
                        fontSize: 11, padding: '2px 8px', borderRadius: 12,
                      }}>GRATUIT</span>
                    </div>
                  );
                })()}

                {/* Grille des SKUs payants */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                  {FABRIC_SKUS.filter(s => !(s as any).trial).map(s => {
                    const isSelected = (params.sku || 'F2') === s.sku;
                    return (
                      <div
                        key={s.sku}
                        onClick={() => handleParamChange('sku', s.sku)}
                        style={{
                          border: `2px solid ${isSelected ? '#038387' : '#E5E7EB'}`,
                          borderRadius: 8,
                          padding: '8px 10px',
                          cursor: 'pointer',
                          background: isSelected ? '#F0FDFE' : 'white',
                          transition: 'all 0.15s',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
                          <span style={{ fontWeight: 700, fontSize: 13, color: isSelected ? '#038387' : '#111827' }}>{s.sku}</span>
                          <span style={{ fontSize: 10, color: '#6B7280' }}>{s.cus} CUs</span>
                        </div>
                        <div style={{ fontSize: 11, color: '#374151' }}>~€{s.monthlyEur.toLocaleString('fr-FR')}/mois</div>
                        <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 2 }}>{s.useCase}</div>
                      </div>
                    );
                  })}
                </div>
                {(() => {
                  const selected = FABRIC_SKUS.find(s => s.sku === (params.sku || 'F2'));
                  if (!selected) return null;
                  const isTrial = (selected as any).trial;
                  return (
                    <div style={{
                      marginTop: 10,
                      padding: '8px 12px',
                      background: isTrial ? '#ECFDF5' : '#F0FDFE',
                      border: `1px solid ${isTrial ? '#10B981' : '#038387'}`,
                      borderRadius: 6,
                      fontSize: 11,
                      color: isTrial ? '#065F46' : '#0F5256',
                    }}>
                      <div style={{ fontWeight: 600, marginBottom: 2 }}>{selected.sku} — {selected.cus} Capacity Units</div>
                      {isTrial
                        ? <div>Capacité d'essai Microsoft Fabric — <strong>Gratuit pendant 60 jours</strong></div>
                        : <div>Estimation : <strong>~€{selected.monthlyEur.toLocaleString('fr-FR')}/mois</strong> (Pay-as-you-go)</div>
                      }
                      <div style={{ color: '#6B7280', marginTop: 3 }}>{selected.useCase}</div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* FABRIC WORKSPACE — RBAC */}
            {isFabricWorkspace && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <label style={{ fontWeight: 700, fontSize: 12 }}>Contrôle d'accès (RBAC)</label>
                  <button
                    onClick={() => {
                      const prev = params.roleAssignments || [];
                      handleParamChange('roleAssignments', [...prev, { email: '', role: 'Viewer' }]);
                    }}
                    style={{ background: '#038387', color: 'white', border: 'none', borderRadius: 5, padding: '3px 10px', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}
                  >
                    + Ajouter
                  </button>
                </div>

                {(!params.roleAssignments || params.roleAssignments.length === 0) && (
                  <div style={{ fontSize: 11, color: '#9CA3AF', padding: '6px 0' }}>
                    Aucun accès configuré — workspace accessible au propriétaire uniquement.
                  </div>
                )}

                {(params.roleAssignments || []).map((a: { email: string; role: string }, idx: number) => (
                  <div key={idx} style={{ display: 'flex', gap: 5, marginBottom: 5, alignItems: 'center' }}>
                    <input
                      type="text"
                      placeholder="user@contoso.com"
                      value={a.email}
                      onChange={(e) => {
                        const upd = [...(params.roleAssignments || [])];
                        upd[idx] = { ...upd[idx], email: e.target.value };
                        handleParamChange('roleAssignments', upd);
                      }}
                      style={{ flex: 1, padding: '5px 8px', borderRadius: 5, border: '1px solid #E5E7EB', fontSize: 11 }}
                    />
                    <select
                      value={a.role}
                      onChange={(e) => {
                        const upd = [...(params.roleAssignments || [])];
                        upd[idx] = { ...upd[idx], role: e.target.value };
                        handleParamChange('roleAssignments', upd);
                      }}
                      style={{ fontSize: 11, padding: '5px 6px', borderRadius: 5, border: '1px solid #E5E7EB', background: 'white', cursor: 'pointer' }}
                    >
                      {['Admin', 'Member', 'Contributor', 'Viewer'].map(r => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => {
                        const upd = (params.roleAssignments || []).filter((_: any, i: number) => i !== idx);
                        handleParamChange('roleAssignments', upd);
                      }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', fontSize: 16, lineHeight: 1, padding: '0 3px' }}
                    >
                      ×
                    </button>
                  </div>
                ))}

                {params.roleAssignments && params.roleAssignments.length > 0 && (
                  <div style={{ fontSize: 10, color: '#6B7280', marginTop: 4, padding: '5px 8px', background: '#F9FAFB', borderRadius: 4, lineHeight: 1.5 }}>
                    <strong>Admin</strong> = gestion complète · <strong>Member</strong> = lecture/écriture · <strong>Contributor</strong> = contribuer · <strong>Viewer</strong> = lecture seule
                  </div>
                )}
              </div>
            )}

            {/* FABRIC DOMAIN — Contributors Scope */}
            {isFabricDomain && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontWeight: 700, fontSize: 12, display: 'block', marginBottom: 8 }}>
                  Contributors Scope
                </label>
                {[
                  { value: 'SpecificUsersAndGroups', label: 'Utilisateurs spécifiques', desc: 'Seuls les membres définis peuvent contribuer au domaine' },
                  { value: 'AllTenant',              label: 'Tout le tenant',           desc: 'Tous les utilisateurs du tenant peuvent contribuer' },
                  { value: 'AdminsOnly',             label: 'Admins uniquement',        desc: 'Seuls les admins Fabric peuvent modifier ce domaine' },
                ].map(opt => {
                  const isSelected = (params.contributorsScope || 'SpecificUsersAndGroups') === opt.value;
                  return (
                    <div
                      key={opt.value}
                      onClick={() => handleParamChange('contributorsScope', opt.value)}
                      style={{
                        border: `2px solid ${isSelected ? '#038387' : '#E5E7EB'}`,
                        borderRadius: 7,
                        padding: '8px 11px',
                        cursor: 'pointer',
                        background: isSelected ? '#F0FDFE' : 'white',
                        marginBottom: 5,
                        transition: 'all 0.15s',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{
                          width: 12, height: 12, borderRadius: '50%',
                          border: `2px solid ${isSelected ? '#038387' : '#D1D5DB'}`,
                          background: isSelected ? '#038387' : 'white',
                          flexShrink: 0,
                        }} />
                        <span style={{ fontSize: 12, fontWeight: isSelected ? 700 : 500, color: isSelected ? '#038387' : '#111827' }}>
                          {opt.label}
                        </span>
                      </div>
                      <div style={{ fontSize: 10, color: '#6B7280', marginTop: 3, paddingLeft: 18 }}>
                        {opt.desc}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* PURVIEW — Déployer nouveau vs Référencer existant */}
            {isPurviewAccount && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontWeight: 700, fontSize: 12, display: 'block', marginBottom: 8 }}>
                  Mode Purview
                </label>
                <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: '1px solid #E5E7EB' }}>
                  {[
                    { value: 'deploy',    label: '➕ Déployer nouveau',      desc: 'Crée une nouvelle instance Microsoft Purview' },
                    { value: 'reference', label: '🔗 Référencer existant',   desc: 'Utilise un compte Purview déjà déployé (data source)' },
                  ].map((opt, idx) => {
                    const isSelected = (params.purviewMode || 'deploy') === opt.value;
                    return (
                      <div
                        key={opt.value}
                        onClick={() => handleParamChange('purviewMode', opt.value)}
                        style={{
                          flex: 1,
                          padding: '8px 12px',
                          cursor: 'pointer',
                          background: isSelected ? '#EFF6FF' : '#F9FAFB',
                          borderRight: idx === 0 ? '1px solid #E5E7EB' : 'none',
                          transition: 'background 0.15s',
                        }}
                      >
                        <div style={{ fontSize: 12, fontWeight: 700, color: isSelected ? '#2563EB' : '#374151' }}>{opt.label}</div>
                        <div style={{ fontSize: 10, color: '#6B7280', marginTop: 2 }}>{opt.desc}</div>
                      </div>
                    );
                  })}
                </div>

                {(params.purviewMode || 'deploy') === 'reference' && (
                  <div style={{ marginTop: 10, padding: '10px 12px', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#1D4ED8', marginBottom: 8 }}>
                      Paramètres du compte existant
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <label style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>
                        Nom du compte Purview <span style={{ color: '#DC2626' }}>*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="mon-purview-account"
                        value={params.name || ''}
                        onChange={(e) => handleParamChange('name', e.target.value)}
                        style={{ ...inputStyle, marginTop: 4, fontSize: 11 }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>
                        Resource Group <span style={{ color: '#DC2626' }}>*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="rg-governance"
                        value={params.resource_group_name || ''}
                        onChange={(e) => handleParamChange('resource_group_name', e.target.value)}
                        style={{ ...inputStyle, marginTop: 4, fontSize: 11 }}
                      />
                    </div>
                    <div style={{ marginTop: 8, fontSize: 10, color: '#1D4ED8' }}>
                      Génère un bloc <code>data "azurerm_purview_account"</code> pour référencer les attributs sans redéployer.
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* LOCATION SECTION — région pour tous les providers */}
            {!isFabricCapacity && <div style={{ marginBottom: 14 }}>
              {resourceConfig ? (
                <FieldWithSuggestions
                  label={tfKey?.startsWith('aws_') ? 'Region (aws_region)' : tfKey?.startsWith('google_') ? 'Region (gcp_region)' : t('settingsPanel.fields.location')}
                  value={String(params.location ?? "")}
                  onChange={(value) => setParams((prev) => ({ ...prev, location: value }))}
                  suggestions={tfKey?.startsWith('aws_') ? ['eu-west-1', 'eu-west-3', 'us-east-1', 'us-west-2', 'ap-southeast-1'] : tfKey?.startsWith('google_') ? ['europe-west1', 'europe-west3', 'us-central1', 'us-east1'] : resourceConfig.fields.find(f => f.key === 'location')?.options || ['eastus', 'westus', 'westeurope', 'northeurope']}
                  placeholder={tfKey?.startsWith('aws_') ? 'eu-west-3' : tfKey?.startsWith('google_') ? 'europe-west1' : t('settingsPanel.fields.locationPlaceholder')}
                  required={resourceConfig.fields.find(f => f.key === 'location')?.required}
                  description={tfKey?.startsWith('aws_') ? 'Région AWS (mappée vers aws_region dans le provider Terraform)' : tfKey?.startsWith('google_') ? 'Région GCP (mappée vers gcp_region dans le provider Terraform)' : t('settingsPanel.fields.locationDescription')}
                  architectureId={activeTabId || undefined}
                />
              ) : isBackendUnavailable ? (
                <div>
                  <label style={{ fontWeight: 700, fontSize: 12 }}>{t('settingsPanel.fields.location')}</label>
                  <input
                    type="text"
                    style={inputStyle}
                    value={params.location ?? ""}
                    onChange={(e) => setParams((prev) => ({ ...prev, location: e.target.value }))}
                    placeholder={t('settingsPanel.fields.locationPlaceholder')}
                  />
                </div>
              ) : (
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  {t('settingsPanel.fields.locationNone')}
                </div>
              )}
            </div>}

            {/* PARAMÈTRES DYNAMIQUES */}
            {!isFabricCapacity && <div style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 700, marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>{t('settingsPanel.parameters.title')}</span>
                {tfData?.properties && tfData.properties.length > 5 && (
                  <button
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    style={{
                      background: showAdvanced ? '#10b981' : '#3b82f6',
                      border: 'none',
                      borderRadius: 6,
                      padding: '6px 12px',
                      fontSize: 12,
                      cursor: 'pointer',
                      color: '#ffffff',
                      fontWeight: 600,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                      transition: 'all 0.2s',
                    }}
                  >
                    {showAdvanced ? t('settingsPanel.parameters.simple') : t('settingsPanel.parameters.advanced')}
                  </button>
                )}
              </div>

              {/* Toutes les propriétés Terraform configurables */}
              {tfData?.properties && Array.isArray(tfData.properties) &&
              tfData.properties.filter((p) => {
                // Exclure location (géré séparément)
                if (p.name === "location") return false;
                // Exclure les propriétés purement read-only (computed sans optional)
                const readOnlyProps = ['id', 'etag', 'self_link', 'creation_timestamp', 'fingerprint', 'kind',
                  'fully_qualified_domain_name', 'restorable_dropped_database_ids', 'primary_user_assigned_identity_id'];
                if (readOnlyProps.includes(p.name)) return false;
                if (p.computed && !p.optional && !p.required) return false;
                return true;
              }).length > 0 ? (
                <>
                  {tfData.properties
                    .filter((p) => {
                      // Exclure location (géré séparément)
                      if (p.name === "location") return false;

                      // Exclure les propriétés purement read-only
                      const readOnlyProps = ['id', 'etag', 'self_link', 'creation_timestamp', 'fingerprint', 'kind',
                        'fully_qualified_domain_name', 'restorable_dropped_database_ids', 'primary_user_assigned_identity_id'];
                      if (readOnlyProps.includes(p.name)) return false;

                      // Exclure les propriétés qui sont UNIQUEMENT computed (lecture seule)
                      if (p.computed && !p.optional && !p.required) return false;

                      // Filtrer par recherche
                      if (searchQuery) {
                        const query = searchQuery.toLowerCase();
                        const matchesName = p.name.toLowerCase().includes(query);
                        const matchesDescription = p.description?.toLowerCase().includes(query);
                        if (!matchesName && !matchesDescription) return false;
                      }

                      // Mode simple: afficher les champs requis ET les propriétés importantes
                      if (!showAdvanced) {
                        // Propriétés toujours visibles (sécurité, réseau, configuration de base)
                        const alwaysShowProps = [
                          'name', 'sku', 'sku_name', 'tier', 'size', 'capacity',
                          'version', 'administrator_login', 'administrator_login_password',
                          'public_network_access_enabled', 'minimum_tls_version',
                          'tags', 'resource_group_name', 'location',
                          'bucket', 'cidr_block', 'instance_type', 'engine', 'identifier',
                        ];
                        return p.required || alwaysShowProps.includes(p.name);
                      }

                      // Mode avancé: TOUTES les propriétés configurables (required + optional)
                      return true;
                    })
                    .map((p) => (
                      <div key={p.name} style={{ marginBottom: 10 }}>
                        <label style={{ fontSize: 12, fontWeight: 600 }}>
                          {p.name}
                          {p.required && (
                            <span style={{ color: "#dc2626", marginLeft: 4 }}>*</span>
                          )}
                        </label>
                        {renderTerraformInput(p)}
                        {p.description && (
                          <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
                            {p.description}
                          </div>
                        )}
                      </div>
                    ))}

                  {/* Afficher les nested blocks (ex: settings, autoscale_settings, etc.) */}
                  {tfData.nestedBlocks && Array.isArray(tfData.nestedBlocks) &&
                    tfData.nestedBlocks
                      .filter((block) => {
                        // Exclure les blocks purement techniques
                        if (block.name === 'timeouts') return false;

                        // Filtrer par recherche (sur le nom du block)
                        if (searchQuery && block.name) {
                          const query = searchQuery.toLowerCase();
                          const matchesBlockName = block.name.toLowerCase().includes(query);
                          // Aussi vérifier si un attribut du block correspond
                          const hasMatchingAttribute = block.attributes?.some(attr =>
                            attr.name.toLowerCase().includes(query) ||
                            attr.description?.toLowerCase().includes(query)
                          );
                          if (!matchesBlockName && !hasMatchingAttribute) return false;
                        }

                        // Inclure tous les autres blocks qui ont des attributs
                        return block.attributes && Array.isArray(block.attributes) && block.attributes.length > 0;
                      })
                      .map((block) => (
                        <div key={block.name} style={{ marginTop: 16, marginBottom: 12 }}>
                          <div style={{
                            fontSize: 13,
                            fontWeight: 700,
                            marginBottom: 8,
                            color: '#4B5563',
                            borderBottom: '1px solid #E5E7EB',
                            paddingBottom: 4
                          }}>
                            {block.name.charAt(0).toUpperCase() + block.name.slice(1)}
                          </div>
                          {block.attributes && Array.isArray(block.attributes) &&
                            block.attributes
                              .filter((attr) => {
                                // Exclure les propriétés techniques read-only
                                const technicalProps = ['id', 'etag'];
                                if (technicalProps.includes(attr.name)) return false;

                                // Filtrer par recherche
                                if (searchQuery) {
                                  const query = searchQuery.toLowerCase();
                                  const matchesName = attr.name.toLowerCase().includes(query);
                                  const matchesDescription = attr.description?.toLowerCase().includes(query);
                                  if (!matchesName && !matchesDescription) return false;
                                }

                                // Inclure les propriétés requises
                                if (attr.required) return true;

                                // Inclure les propriétés optionnelles
                                if (attr.optional) return true;

                                // Exclure les propriétés uniquement computed
                                if (attr.computed && !attr.optional) return false;

                                return true;
                              })
                              .map((attr) => (
                                <div key={`${block.name}.${attr.name}`} style={{ marginBottom: 10 }}>
                                  <label style={{ fontSize: 12, fontWeight: 600 }}>
                                    {attr.name}
                                    {attr.required && (
                                      <span style={{ color: "#dc2626", marginLeft: 4 }}>*</span>
                                    )}
                                  </label>
                                  {renderTerraformInput(attr)}
                                  {attr.description && (
                                    <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
                                      {attr.description}
                                    </div>
                                  )}
                                </div>
                              ))}
                        </div>
                      ))}

                  {/* Section Paramètres d'estimation (champs virtuels pour le calcul des coûts) */}
                  {resourceConfig?.fields && resourceConfig.fields.filter(f =>
                    !tfData.properties?.some(p => p.name === f.key) &&
                    f.key !== 'location' &&
                    f.key !== 'name' &&
                    f.key !== 'resource_group_name'
                  ).length > 0 && (
                    <div style={{ marginTop: 16, marginBottom: 12 }}>
                      <div style={{
                        fontSize: 13,
                        fontWeight: 700,
                        marginBottom: 8,
                        color: '#059669',
                        borderBottom: '1px solid #10B981',
                        paddingBottom: 4
                      }}>
                        {t('settingsPanel.parameters.estimation')}
                      </div>
                      {resourceConfig.fields
                        .filter(f =>
                          !tfData.properties?.some(p => p.name === f.key) &&
                          f.key !== 'location' &&
                          f.key !== 'name' &&
                          f.key !== 'resource_group_name'
                        )
                        .map((field) => (
                          <div key={field.key} style={{ marginBottom: 10 }}>
                            <label style={{ fontSize: 12, fontWeight: 600 }}>
                              {field.label}
                              {field.required && <span style={{ color: "#dc2626", marginLeft: 4 }}>*</span>}
                            </label>
                            {field.type === 'select' ? (
                              <select
                                style={selectStyle}
                                value={params[field.key] ?? field.default ?? ""}
                                onChange={(e) => handleParamChange(field.key, e.target.value)}
                              >
                                {field.placeholder && <option value="">{field.placeholder}</option>}
                                {field.options?.map(opt => (
                                  <option key={opt} value={opt}>{opt}</option>
                                ))}
                              </select>
                            ) : field.type === 'number' ? (
                              <input
                                type="number"
                                style={inputStyle}
                                value={params[field.key] ?? field.default ?? ""}
                                onChange={(e) => handleParamChange(field.key, e.target.value ? Number(e.target.value) : "")}
                                placeholder={field.placeholder}
                              />
                            ) : (
                              <FieldWithSuggestions
                                label=""
                                value={String(params[field.key] ?? "")}
                                onChange={(value) => handleParamChange(field.key, value)}
                                suggestions={field.suggestions}
                                placeholder={field.placeholder}
                                required={field.required}
                                architectureId={activeTabId || undefined}
                              />
                            )}
                            {field.description && (
                              <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
                                {field.description}
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  )}

                  {tfData.description && (
                    <div style={{
                      marginTop: 12,
                      padding: 8,
                      background: '#F0F9FF',
                      border: '1px solid #3B82F6',
                      borderRadius: 6,
                      fontSize: 11,
                      color: '#1E40AF',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 6,
                    }}>
                      <FiInfo size={14} style={{ marginTop: 1, flexShrink: 0 }} />
                      <div>{tfData.description}</div>
                    </div>
                  )}
                  <div style={{
                    marginTop: 8,
                    padding: 6,
                    background: '#ECFDF5',
                    border: '1px solid #10B981',
                    borderRadius: 6,
                    fontSize: 10,
                    color: '#065F46',
                  }}>
                    {t('settingsPanel.messages.loadedFromTerraform')}
                  </div>
                </>

              ) : isBackendUnavailable || !tfData ? (
                /* Mode fallback - utiliser la configuration locale */
                <>
                  {resourceConfig ? (
                    /* Utiliser la configuration complète avec suggestions */
                    <>
                      {resourceConfig.fields.filter(f => f.key !== 'location').map((field) => {
                        if (field.type === 'select') {
                          return (
                            <div key={field.key} style={{ marginBottom: 10 }}>
                              <label style={{ fontSize: 12, fontWeight: 600 }}>
                                {field.label}
                                {field.required && <span style={{ color: "#dc2626", marginLeft: 4 }}>*</span>}
                              </label>
                              <select
                                style={selectStyle}
                                value={params[field.key] ?? field.default ?? ""}
                                onChange={(e) => handleParamChange(field.key, e.target.value)}
                              >
                                {field.placeholder && <option value="">{field.placeholder}</option>}
                                {field.options?.map(opt => (
                                  <option key={opt} value={opt}>{opt}</option>
                                ))}
                              </select>
                              {field.description && (
                                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
                                  {field.description}
                                </div>
                              )}
                            </div>
                          );
                        }

                        return (
                          <FieldWithSuggestions
                            key={field.key}
                            label={field.label}
                            value={String(params[field.key] ?? "")}
                            onChange={(value) => handleParamChange(field.key, value)}
                            suggestions={field.suggestions}
                            placeholder={field.placeholder}
                            required={field.required}
                            description={field.description}
                            type={field.type === 'number' ? 'number' : 'text'}
                            architectureId={activeTabId || undefined}
                          />
                        );
                      })}
                      {resourceConfig.description && (
                        <div style={{
                          marginTop: 12,
                          padding: 8,
                          background: '#F0F9FF',
                          border: '1px solid #3B82F6',
                          borderRadius: 6,
                          fontSize: 11,
                          color: '#1E40AF',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 6,
                        }}>
                          <FiInfo size={14} style={{ marginTop: 1, flexShrink: 0 }} />
                          <div>{resourceConfig.description}</div>
                        </div>
                      )}
                    </>
                  ) : (
                    /* Fallback de base - afficher tous les paramètres existants */
                    <>
                      {/* Champs communs avec suggestions */}
                      <FieldWithSuggestions
                        label={t('settingsPanel.fields.name')}
                        value={String(params.name ?? "")}
                        onChange={(value) => handleParamChange('name', value)}
                        suggestions={[`${tfKey?.replace('azurerm_', '')}-myapp`, `${tfKey?.replace('azurerm_', '')}-prod`]}
                        placeholder={t('settingsPanel.fields.namePlaceholder')}
                        required={true}
                        architectureId={activeTabId || undefined}
                      />

                      <FieldWithSuggestions
                        label={t('settingsPanel.fields.resourceGroup')}
                        value={String(params.resource_group_name ?? "")}
                        onChange={(value) => handleParamChange('resource_group_name', value)}
                        suggestions={availableNodes.length > 0 ? getResourceGroupNames(availableNodes, node) : ['rg-prod', 'rg-dev', 'rg-infrastructure']}
                        placeholder={t('settingsPanel.fields.resourceGroupPlaceholder')}
                        architectureId={activeTabId || undefined}
                      />

                      {/* Afficher tous les autres paramètres existants */}
                      {Object.keys(params)
                        .filter(key => key !== 'name' && key !== 'resource_group_name' && key !== 'location')
                        .map((key) => {
                          // Special: workspaceId for Fabric items → dropdown of canvas workspaces
                          if (key === 'workspaceId' && isFabricItem) {
                            // Fallback: if no explicit workspaceId param, use parentNode if it's a workspace
                            const resolvedWsId = params.workspaceId ||
                              (node.parentNode && fabricWorkspaceOptions.some(w => w.id === node.parentNode)
                                ? node.parentNode : '');
                            return (
                              <div key={key} style={{ marginBottom: 10 }}>
                                <label style={{ fontSize: 12, fontWeight: 600 }}>
                                  WorkspaceId
                                  <span style={{ fontSize: 10, fontWeight: 400, color: '#6B7280', marginLeft: 4 }}>— workspace parent</span>
                                </label>
                                <select
                                  style={{ ...selectStyle, marginTop: 4 }}
                                  value={resolvedWsId}
                                  onChange={(e) => handleParamChange('workspaceId', e.target.value)}
                                >
                                  <option value="">-- Sélectionnez un workspace --</option>
                                  {fabricWorkspaceOptions.map(ws => (
                                    <option key={ws.id} value={ws.id}>{ws.label}</option>
                                  ))}
                                </select>
                                {fabricWorkspaceOptions.length === 0 && (
                                  <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 3 }}>
                                    Aucune ressource disponible
                                  </div>
                                )}
                              </div>
                            );
                          }

                          // Special: capacityId for Fabric workspaces → dropdown of canvas capacities
                          if (key === 'capacityId' && isFabricWorkspace) {
                            // Fallback: if no explicit capacityId param, use parentNode if it's a capacity
                            const resolvedCapId = params.capacityId ||
                              (node.parentNode && fabricCapacityOptions.some(c => c.id === node.parentNode)
                                ? node.parentNode : '');
                            return (
                              <div key={key} style={{ marginBottom: 10 }}>
                                <label style={{ fontSize: 12, fontWeight: 600 }}>
                                  CapacityId
                                  <span style={{ fontSize: 10, fontWeight: 400, color: '#6B7280', marginLeft: 4 }}>— capacity parente</span>
                                </label>
                                <select
                                  style={{ ...selectStyle, marginTop: 4 }}
                                  value={resolvedCapId}
                                  onChange={(e) => handleParamChange('capacityId', e.target.value)}
                                >
                                  <option value="">-- Sélectionnez une capacity --</option>
                                  {fabricCapacityOptions.map(cap => (
                                    <option key={cap.id} value={cap.id}>
                                      {cap.label}{cap.sku ? ` (${cap.sku})` : ''}
                                    </option>
                                  ))}
                                </select>
                                {fabricCapacityOptions.length === 0 && (
                                  <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 3 }}>
                                    Aucune capacity disponible
                                  </div>
                                )}
                              </div>
                            );
                          }

                          // Check if this param is a resource reference
                          const referencedResourceType = getResourceReferenceType(key);
                          const resourceReferences = referencedResourceType && availableNodes.length > 0
                            ? getResourceReferences(availableNodes, referencedResourceType, node.id)
                            : [];

                          return (
                            <FieldWithSuggestions
                              key={key}
                              label={key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              value={String(params[key] ?? "")}
                              onChange={(value) => handleParamChange(key, value)}
                              suggestions={resourceReferences.length > 0 ? resourceReferences : []}
                              placeholder={resourceReferences.length > 0 ? `Sélectionnez ${referencedResourceType?.replace(/^(azurerm_|aws_|google_)/, '')}` : `Entrez ${key}`}
                              architectureId={activeTabId || undefined}
                            />
                          );
                        })}

                      <div style={{
                        marginTop: 12,
                        padding: 8,
                        background: '#FEF3C7',
                        border: '1px solid #F59E0B',
                        borderRadius: 6,
                        fontSize: 11,
                        color: '#92400E'
                      }}
                        dangerouslySetInnerHTML={{ __html: t('settingsPanel.messages.basicModeConfig') }}
                      />
                    </>
                  )}
                </>
              ) : (
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  {t('settingsPanel.parameters.noEditable')}
                </div>
              )}
            </div>}

          {/* 👇👇👇 AJOUTE CETTE LIGNE POUR FERMER LE FRAGMENT */}
          </>
        )}
      </div>

      {/* FOOTER */}
      <div
        style={{
          position: isDock ? "sticky" : "static",
          bottom: 0,
          zIndex: 2,
          padding: 12,
          background: "#fff",
          borderTop: "1px solid #e5e7eb",
        }}
      >
        {saveSuccess && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 12px',
            marginBottom: 8,
            borderRadius: 6,
            background: '#f0fdf4',
            border: '1px solid #86efac',
            color: '#15803d',
            fontSize: 13,
            fontWeight: 500,
            animation: 'fadeInUp 0.2s ease',
          }}>
            <FiCheck size={15} />
            Paramètres enregistrés avec succès
          </div>
        )}
        <button onClick={handleSave} style={saveBtnStyle}>
          {t('settingsPanel.buttons.save')}
        </button>
      </div>
    </div>
  );
}

/* ------------------ Styles ------------------ */
const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  marginTop: 4,
  border: "1px solid #e5e7eb",
  borderRadius: 8,
  fontSize: 12,
};

const selectStyle: React.CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  marginTop: 4,
  border: "1px solid #e5e7eb",
  borderRadius: 8,
  fontSize: 12,
  background: "#fff",
};

const saveBtnStyle: React.CSSProperties = {
  width: "100%",
  padding: "10px 12px",
  backgroundColor: "#2563eb",
  color: "#fff",
  border: "none",
  borderRadius: 8,
  cursor: "pointer",
  fontWeight: 700,
  fontSize: 13,
};
